import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService, PaginatedResponse } from './api.service';
import { PluginAccess, User, UserStatus, UpdateProfileRequest, ChangePasswordRequest } from '../../shared/models';

export interface CreateUserRequest {
  email: string;
  fullName?: string;
  position?: string;
  roleId: string;
  password?: string;
  mustChangePassword?: boolean;
}

export interface UpdateUserRequest {
  fullName?: string;
  position?: string;
  roleId?: string;
}

export interface UserQueryParams {
  page?: number;
  size?: number;
  search?: string;
  roleId?: string;
  status?: UserStatus;
  sort?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly endpoint = '/users';

  private readonly api = inject(ApiService);

  getUsers(params?: UserQueryParams): Observable<PaginatedResponse<User>> {
    return this.api.get<PaginatedResponse<User>>(this.endpoint, { params: params as Record<string, string | number | boolean>, skipToast: true });
  }

  getUserById(id: string): Observable<User> {
    return this.api.get<User>(`${this.endpoint}/${id}`, { skipToast: true });
  }

  createUser(user: CreateUserRequest): Observable<User> {
    return this.api.post<User>(this.endpoint, user, { skipToast: true });
  }

  updateUser(id: string, user: UpdateUserRequest): Observable<User> {
    return this.api.put<User>(`${this.endpoint}/${id}`, user, { skipToast: true });
  }

  deleteUser(id: string): Observable<void> {
    return this.api.delete<void>(`${this.endpoint}/${id}`, { skipToast: true });
  }

  deactivateUser(id: string): Observable<User> {
    return this.api.patch<User>(`${this.endpoint}/${id}/deactivate`, {}, { skipToast: true });
  }

  activateUser(id: string): Observable<User> {
    return this.api.patch<User>(`${this.endpoint}/${id}/activate`, {}, { skipToast: true });
  }

  resetPassword(id: string, newPassword: string): Observable<void> {
    return this.api.patch<void>(`${this.endpoint}/${id}/reset-password`, { newPassword }, { skipToast: true });
  }

  assignRole(id: string, roleId: string): Observable<User> {
    return this.api.patch<User>(`${this.endpoint}/${id}/assign-role`, { roleId }, { skipToast: true });
  }

  getUserPluginAccess(id: string): Observable<PluginAccess[]> {
    return this.api.get<PluginAccess[]>(`${this.endpoint}/${id}/plugins`, { skipToast: true });
  }

  updateUserPluginAccess(id: string, pluginAccess: PluginAccess[]): Observable<{ message: string }> {
    return this.api.put<{ message: string }>(`${this.endpoint}/${id}/plugins`, pluginAccess, { skipToast: true });
  }



  // Profile management methods
  updateProfile(profileData: UpdateProfileRequest): Observable<User> {
    return this.api.put<User>('/auth/profile', profileData);
  }

  changePassword(passwordData: ChangePasswordRequest): Observable<void> {
    return this.api.post<void>('/auth/change-password', passwordData);
  }

  uploadPhoto(file: File): Observable<{photoUrl: string}> {
    const formData = new FormData();
    formData.append('photo', file);
    return this.api.post<{photoUrl: string}>('/auth/profile/photo', formData);
  }

}
